import { FC } from 'react';
import styles from './CurrentPrice.module.css';
import Table from '@/components/Tables/Table/Table';
import { CurrentPriceProps } from './CurrentPrice.types';
import _ from 'lodash';
import { CellContext } from '@tanstack/react-table';
import { GroupPrices } from '@/types/menus.types';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip } from '@mui/material';
import { AlertTriangle } from 'react-feather';

const CurrentPrice: FC<CurrentPriceProps> = ({
  tableData,
  setPriceDeviationsData,
  storeProducts,
  deactivateProduct,
}) => {
  const { t } = useTranslation();
  const StoreGroupLabel = ({ row }: CellContext<GroupPrices, unknown>) => (
    <div>
      {row.original.selectedStoreCount === row.original.totalStoreCount ? (
        <span>{row.original.storeGroupName}</span>
      ) : (
        <span className={styles.storeNameAndCount}>
          {`${row.original.storeGroupName}`} &nbsp;
          <div
            className={styles.storeCountStyle}
          >{`(${row.original.selectedStoreCount} / ${row.original.totalStoreCount})`}</div>
        </span>
      )}
    </div>
  );

  const getInfoOutlined = (storeGroupName: any, selectedVariants: (string | undefined)[]) => (
    <InfoOutlined
      className={styles.priceRangeInfo}
      sx={{
        width: '22px',
        height: '22px',
        color: '#266DCC',
      }}
      onClick={() =>
        setPriceDeviationsData({
          selectedGroupName: storeGroupName,
          variantIds: selectedVariants as never[],
          isPriceDeviationsDrawerOpen: true,
          cameFromEditDrawer: true,
        })
      }
    />
  );

  const PriceRanges = ({ row }: CellContext<GroupPrices, unknown>) => {
    const priceList = row.original.prices;
    const { storeGroupName } = row.original;
    const selectedVariants = storeProducts.map((item) => item.variantId);
    const sortPrices = () => {
      if (priceList?.length >= 2) {
        for (let i = 0; i < priceList.length; i += 1) {
          if (priceList[i]?.includes('-')) {
            const [first, last] = priceList[i].split('-');
            priceList.splice(i, 1, first, last);
          }
        }
        priceList.sort((a: any, b: any) => a - b);
        return (
          !priceList.every((price) => price === row.original.prices[0]) && (
            <div className={styles.priceRange}>
              {`$${priceList[0]} - $${priceList[priceList.length - 1]} `}
              {getInfoOutlined(storeGroupName, selectedVariants)}
            </div>
          )
        );
      }
      if (priceList?.length === 1 && priceList[0]?.includes('-')) {
        const [first, last] = priceList[0].split('-');
        priceList.splice(0, 1, first, last);
        priceList.sort((a: any, b: any) => a - b);
        return (
          <div className={styles.priceRange}>
            {`$${priceList[0]} - $${priceList[priceList.length - 1]} `}{' '}
            {getInfoOutlined(storeGroupName, selectedVariants)}
          </div>
        );
      }
      return <div>{`$${priceList?.[0]}`}</div>;
    };
    return (
      <div>
        {row.original.prices?.length >= 2 &&
          row.original.prices.every((price) => price === row.original.prices[0]) &&
          !row.original.prices[0]?.includes('-') && <div>${row.original.prices[0]}</div>}
        {sortPrices()}
      </div>
    );
  };

  return (
    <div>
      <div className={styles.productInfo}>
        {deactivateProduct && (
          <Chip
            avatar={
              <Avatar style={{ width: '35px', height: '35px', backgroundColor: '#FAE4D0', padding: '1px' }}>
                <AlertTriangle color="#E6740A" />
              </Avatar>
            }
            data-testid="icon"
            key="deactivateProductWarning"
            label={t('editMenuPrice.productDeactivationWarning')}
            color="default"
            variant="outlined"
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              textAlign: 'left',
              height: '40px',
              backgroundColor: '#FAE4D0',
              color: '#252525',
              borderColor: '#E6740A',
              width: '100%',
              justifyContent: 'left',
              mt: '10px',
              mb: '20px',
            }}
          />
        )}
        {t('editMenuPrice.currentPrice')}
        <Table
          data={tableData}
          columns={[
            {
              accessorKey: 'storeGroupName',
              header: 'Store Groups',
              cell: StoreGroupLabel,
            },
            {
              accessorKey: 'prices',
              header: 'Price',
              cell: PriceRanges,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default CurrentPrice;
